import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/api";
import { Wind, Droplets, Eye, Thermometer, Umbrella, Navigation, X, RefreshCw, CloudSun } from "lucide-react";

// WMO weather code -> Dutch label + emoji-free glyph
const WMO = {
  0: { t: "Helder", i: "☀️" }, 1: { t: "Overwegend helder", i: "🌤️" }, 2: { t: "Half bewolkt", i: "⛅" }, 3: { t: "Bewolkt", i: "☁️" },
  45: { t: "Mist", i: "🌫️" }, 48: { t: "Aanvriezende mist", i: "🌫️" },
  51: { t: "Lichte motregen", i: "🌦️" }, 53: { t: "Motregen", i: "🌦️" }, 55: { t: "Dichte motregen", i: "🌧️" },
  56: { t: "IJzel (licht)", i: "🌧️" }, 57: { t: "IJzel", i: "🌧️" },
  61: { t: "Lichte regen", i: "🌦️" }, 63: { t: "Regen", i: "🌧️" }, 65: { t: "Zware regen", i: "🌧️" },
  66: { t: "IJzel", i: "🌧️" }, 67: { t: "Zware ijzel", i: "🌧️" },
  71: { t: "Lichte sneeuw", i: "🌨️" }, 73: { t: "Sneeuw", i: "🌨️" }, 75: { t: "Zware sneeuw", i: "❄️" }, 77: { t: "Sneeuwkorrels", i: "🌨️" },
  80: { t: "Lichte buien", i: "🌦️" }, 81: { t: "Buien", i: "🌧️" }, 82: { t: "Zware buien", i: "⛈️" },
  85: { t: "Sneeuwbuien", i: "🌨️" }, 86: { t: "Zware sneeuwbuien", i: "❄️" },
  95: { t: "Onweer", i: "⛈️" }, 96: { t: "Onweer met hagel", i: "⛈️" }, 99: { t: "Zwaar onweer met hagel", i: "⛈️" },
};
const wmo = (c) => WMO[c] || { t: "—", i: "🌡️" };

// wind direction (deg, "from") -> compass label
const compass = (deg) => {
  if (deg == null) return "—";
  const dirs = ["N", "NNO", "NO", "ONO", "O", "OZO", "ZO", "ZZO", "Z", "ZZW", "ZW", "WZW", "W", "WNW", "NW", "NNW"];
  return dirs[Math.round(deg / 22.5) % 16];
};
const round = (v) => (v == null ? "—" : Math.round(v));
const fmtHour = (iso) => { try { return new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export function WeatherWidget({ lat, lng, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (lat == null || lng == null) return;
    setLoading(true); setErr(false);
    try {
      const { data } = await axios.get(`${API}/weather`, { params: { lat, lng } });
      setData(data);
    } catch { setErr(true); }
    setLoading(false);
  }, [lat, lng]);

  useEffect(() => { load(); const t = setInterval(load, 10 * 60 * 1000); return () => clearInterval(t); }, [load]);

  if (lat == null || lng == null) return null;

  const cur = data?.current;
  const w = cur ? wmo(cur.weather_code) : null;

  return (
    <div className={`rounded-2xl border border-sky-200 bg-gradient-to-br from-sky-50 to-white ${compact ? "p-3" : "p-4"} shadow-sm`} data-testid="weather-widget">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><CloudSun className="h-4 w-4 text-sky-600" /> Weer ter plaatse</div>
        <div className="flex items-center gap-1">
          <button data-testid="weather-refresh-btn" onClick={load} title="Verversen" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button data-testid="weather-details-btn" onClick={() => setOpen(true)} className="rounded-lg border border-sky-200 bg-white px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-50">
            Details
          </button>
        </div>
      </div>

      {err ? (
        <div className="py-2 text-sm text-slate-400" data-testid="weather-error">Weergegevens niet beschikbaar.</div>
      ) : !cur ? (
        <div className="py-2 text-sm text-slate-400">Laden…</div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <span className="text-3xl leading-none" aria-hidden>{w.i}</span>
            <div>
              <div className="text-2xl font-bold text-slate-900" data-testid="weather-temp">{round(cur.temperature)}°C</div>
              <div className="text-xs text-slate-500">{w.t} · gevoel {round(cur.apparent)}°</div>
            </div>
            <div className="ml-auto flex items-center gap-1.5 rounded-xl bg-white/70 px-2.5 py-1.5" title="Windrichting & snelheid">
              <Navigation className="h-4 w-4 text-slate-600" style={{ transform: `rotate(${(cur.wind_direction ?? 0) + 180}deg)` }} />
              <div className="text-right">
                <div className="text-sm font-bold text-slate-800" data-testid="weather-wind">{round(cur.wind_speed)} km/u</div>
                <div className="text-[11px] text-slate-500">{compass(cur.wind_direction)}{cur.wind_gusts != null ? ` · ${round(cur.wind_gusts)} vlagen` : ""}</div>
              </div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-white/70 px-2 py-1.5"><Umbrella className="mx-auto h-4 w-4 text-sky-500" /><div className="mt-0.5 text-sm font-semibold text-slate-800">{cur.precipitation ?? 0} mm</div><div className="text-[10px] uppercase tracking-wide text-slate-400">Neerslag</div></div>
            <div className="rounded-xl bg-white/70 px-2 py-1.5"><Droplets className="mx-auto h-4 w-4 text-sky-500" /><div className="mt-0.5 text-sm font-semibold text-slate-800">{round(cur.humidity)}%</div><div className="text-[10px] uppercase tracking-wide text-slate-400">Vocht</div></div>
            <div className="rounded-xl bg-white/70 px-2 py-1.5"><Eye className="mx-auto h-4 w-4 text-sky-500" /><div className="mt-0.5 text-sm font-semibold text-slate-800">{cur.visibility != null ? `${(cur.visibility / 1000).toFixed(1)} km` : "—"}</div><div className="text-[10px] uppercase tracking-wide text-slate-400">Zicht</div></div>
          </div>
        </>
      )}

      {open && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)} data-testid="weather-modal">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-base font-bold text-slate-900"><CloudSun className="h-5 w-5 text-sky-600" /> Weersverwachting</div>
              <button data-testid="weather-modal-close" onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>

            {cur && (
              <div className="mb-4 flex items-center gap-4 rounded-xl bg-sky-50 p-3">
                <span className="text-4xl" aria-hidden>{w.i}</span>
                <div>
                  <div className="text-3xl font-bold text-slate-900">{round(cur.temperature)}°C</div>
                  <div className="text-sm text-slate-500">{w.t} · gevoelstemperatuur {round(cur.apparent)}°</div>
                </div>
                <div className="ml-auto space-y-1 text-right text-xs text-slate-600">
                  <div className="flex items-center justify-end gap-1"><Wind className="h-3.5 w-3.5" /> {round(cur.wind_speed)} km/u {compass(cur.wind_direction)}</div>
                  <div className="flex items-center justify-end gap-1"><Droplets className="h-3.5 w-3.5" /> {round(cur.humidity)}%</div>
                  <div className="flex items-center justify-end gap-1"><Thermometer className="h-3.5 w-3.5" /> vlagen {round(cur.wind_gusts)} km/u</div>
                </div>
              </div>
            )}

            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Komende uren</div>
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="weather-forecast">
              {(data?.forecast || []).map((f, i) => {
                const fw = wmo(f.weather_code);
                return (
                  <div key={i} className="flex min-w-[68px] flex-col items-center rounded-xl border border-slate-100 bg-slate-50 px-2 py-2 text-center">
                    <div className="text-[11px] font-semibold text-slate-500">{i === 0 ? "Nu" : fmtHour(f.time)}</div>
                    <div className="my-1 text-xl" aria-hidden>{fw.i}</div>
                    <div className="text-sm font-bold text-slate-800">{round(f.temperature)}°</div>
                    <div className="mt-0.5 flex items-center gap-0.5 text-[10px] text-sky-600"><Umbrella className="h-3 w-3" />{f.precipitation_probability ?? 0}%</div>
                    <div className="mt-0.5 flex items-center gap-0.5 text-[10px] text-slate-500"><Navigation className="h-2.5 w-2.5" style={{ transform: `rotate(${(f.wind_direction ?? 0) + 180}deg)` }} />{round(f.wind_speed)}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-right text-[10px] text-slate-400">Bron: Open-Meteo</div>
          </div>
        </div>
      )}
    </div>
  );
}
